import CreateEpd from 'components/dashboard/CreateEpd';
import PageLoader from 'components/spinners/PageLoader';
import FreshworksWidget from 'components/widgets/FreshworksWidget';
import { toaster } from 'components/v2/toast';
import { ProcessStatus, defaultLicensee } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import useDebounce from 'hooks/useDebounce';
import helpIcon from 'images/icons/svg/warning.svg';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import { AccountModel, EPDStatusDisplayName, InvoiceSummaryModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import InvoiceService from 'services/InvoiceService';
import { FeatureFlagKeyDynamicFormsPortal } from 'services/api/featureFlagsApi';
import { useDashboardQuickStats, useDashboardSearch, useFeatureFlags } from 'services/api/queries';
import {
  Container,
  DashboardLayout,
  DashboardContent,
  DashboardFiltersRow,
  DashboardSideColumn,
  MainView,
} from 'styles/Styles.styled';
import {
  DashboardItemType,
  DashboardListItemModel,
  DashboardQuickStatsRequest,
  DashboardSearchRequest,
  DashboardSortField,
  DashboardViewMode as DashboardApiViewMode,
} from 'types/dashboard/DashboardListItemModel';
import { Option } from 'types/types';
import { PoorMansError } from 'util/utils';
import { getApiErrorDetail, getApiErrorStatus } from 'util/apiErrorUtils';
import { AccountSwitcher } from './AccountSwitcher';
import DashboardFilters from './components/DashboardFilters';
import DynamicEpdCard from './DynamicEpdCard';
import EpdCard from './EpdCard';
import EpdGroupCard from './EpdGroupCard';
import NoEPDs from './NoEPDs';
import Sidebox from './Sidebox';
import { CardContainer, ListViewContainer, CardsEmpty, HeadingArea, InfoBox } from './styles';
import DashboardCompanySelector from './components/DashboardCompanySelector';
import DashboardViewToggle, { DashboardViewMode } from './components/DashboardViewToggle';
import DashboardTypeToggle, { DashboardType } from './components/DashboardTypeToggle';
import OrganisationSettingsButton from './components/OrganisationSettingsButton';
import NewInvitesButton from './components/NewInvitesButton';
import DashboardCardsPaginator, {
  LIST_VIEW_PAGE_SIZE_OPTIONS,
  CARD_VIEW_PAGE_SIZE_OPTIONS,
} from './components/DashboardCardsPaginator';
import DashboardListView from './components/DashboardListView';
import MembershipService from 'services/MembershipService';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import WelcomeToPortal from './WelcomeToPortal';

const allStatusFilters = Object.values(EPDStatusDisplayName);

const FORMAT_STANDARD = 'standard';
const FORMAT_COMPILER = 'compiler';

const DASHBOARD_VIEW_MODE_KEY = 'dashboardViewMode';
const DASHBOARD_TYPE_KEY = 'dashboardType';

// Maps the sortable DashboardListView table columns to the backend's DashboardSortField.
const LIST_SORT_FIELD_MAP: Record<string, DashboardSortField> = {
  registrationNumber: DashboardSortField.RegistrationNumber,
  versionDate: DashboardSortField.VersionDate,
  validityDate: DashboardSortField.ValidityDate,
};

const getInitialViewMode = (): DashboardViewMode => {
  const savedViewMode = localStorage.getItem(DASHBOARD_VIEW_MODE_KEY);
  return savedViewMode === DashboardViewMode.List ? DashboardViewMode.List : DashboardViewMode.Cards;
};

const getInitialDashboardType = (): DashboardType => {
  const savedType = localStorage.getItem(DASHBOARD_TYPE_KEY);
  return savedType === DashboardType.CopyGroups ? DashboardType.CopyGroups : DashboardType.Epds;
};

const Dashboard: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [account] = useState<AccountModel | undefined>({});
  const [filter, setFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<EPDStatusDisplayName[]>([]);
  const [formatFilters, setFormatFilters] = useState<Option[]>([]);
  const [viewMode, setViewMode] = useState<DashboardViewMode>(getInitialViewMode);
  const [dashboardType, setDashboardType] = useState<DashboardType>(getInitialDashboardType);
  const [isAdminLicenseeCompanies, setIsAdminLicenseeCompaies] = useState<boolean>(false);
  const [invitationsCount, setInvitationsCount] = useState<number>(0);
  const [isInvitationsCountLoading, setIsInvitationsCountLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: CARD_VIEW_PAGE_SIZE_OPTIONS[0].value });
  const [paginatorFirst, setPaginatorFirst] = useState(0);
  const [listSortField, setListSortField] = useState<string | undefined>(undefined);
  const [listSortOrder, setListSortOrder] = useState<1 | -1 | null>(null);
  const isAdmin = AuthService.isAdmin();
  const isLicenseeAdmin = AuthService.isLicenseeAdmin();

  const { companyId, company, companyAccountId, licensee } = useContext(CompanyContext);
  // useCompany resolves companyId to '' when the user belongs to no usable organisation and to an id
  // otherwise, so it alone distinguishes all three states: unresolved / no organisation / ready.
  const isCompanyResolved = companyId !== undefined;
  const hasCompany = !!companyId;
  const showWelcomeToPortal = isCompanyResolved && !hasCompany;

  const debouncedFilter = useDebounce(filter, 400);

  const changeViewMode = (newViewMode: DashboardViewMode) => {
    setViewMode(newViewMode);
    localStorage.setItem(DASHBOARD_VIEW_MODE_KEY, newViewMode);
    setFilter('');
    setTypeFilter([]);
    setFormatFilters([]);
  };

  const changeDashboardType = (newType: DashboardType) => {
    setDashboardType(newType);
    localStorage.setItem(DASHBOARD_TYPE_KEY, newType);
    setFilter('');
    setTypeFilter([]);
    setFormatFilters([]);
  };

  const isCopyGroupsView = dashboardType === DashboardType.CopyGroups;
  // Copy Groups mode and the no-organisation welcome layout are always card view; this drives all viewMode-dependent logic below.
  const isCardView = isCopyGroupsView || !hasCompany || viewMode === DashboardViewMode.Cards;

  // Reset pagination to first page with the correct default page size when switching between card and list mode
  useEffect(() => {
    const defaultPageSize = isCardView ? CARD_VIEW_PAGE_SIZE_OPTIONS[0].value : LIST_VIEW_PAGE_SIZE_OPTIONS[0].value;
    setPagination({ page: 1, pageSize: defaultPageSize });
    setPaginatorFirst(0);
    setLastTotalCount(0);
  }, [isCardView]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === DASHBOARD_VIEW_MODE_KEY) {
        setViewMode(e.newValue === DashboardViewMode.List ? DashboardViewMode.List : DashboardViewMode.Cards);
      }
      if (e.key === DASHBOARD_TYPE_KEY) {
        setDashboardType(e.newValue === DashboardType.CopyGroups ? DashboardType.CopyGroups : DashboardType.Epds);
      }
      if (e.key === DASHBOARD_VIEW_MODE_KEY || e.key === DASHBOARD_TYPE_KEY) {
        setFilter('');
        setTypeFilter([]);
        setFormatFilters([]);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    setFilter('');
    setTypeFilter([]);
    setFormatFilters([]);
    setLastTotalCount(0);
  }, [companyId]);

  const featureFlagsQuery = useFeatureFlags();
  const isDynamicFormsEnabled = featureFlagsQuery.data?.[FeatureFlagKeyDynamicFormsPortal] === true;

  const apiViewMode = isCopyGroupsView ? DashboardApiViewMode.CopyGroups : DashboardApiViewMode.Epds;
  const selectedFormatValues = formatFilters.map((x) => x.value as string);

  const dashboardSearchRequest: DashboardSearchRequest = {
    accountId: companyAccountId,
    companyId,
    viewMode: apiViewMode,
    includeDynamicFormDocuments: isDynamicFormsEnabled,
    searchTerm: debouncedFilter || undefined,
    statuses: isCopyGroupsView ? [] : typeFilter,
    formats: isCopyGroupsView ? [] : selectedFormatValues,
    sortField: !isCardView && listSortField ? LIST_SORT_FIELD_MAP[listSortField] : undefined,
    sortOrder: listSortOrder ?? 1,
    pageNumber: pagination.page,
    pageSize: pagination.pageSize,
  };

  const isDashboardDataEnabled = !!companyId && !!companyAccountId && !featureFlagsQuery.isLoading;

  const dashboardQuery = useDashboardSearch(dashboardSearchRequest, isDashboardDataEnabled);

  const quickStatsRequest: DashboardQuickStatsRequest = {
    accountId: companyAccountId,
    companyId,
    viewMode: apiViewMode,
    includeDynamicFormDocuments: isDynamicFormsEnabled,
  };

  const quickStatsQuery = useDashboardQuickStats(quickStatsRequest, isDashboardDataEnabled);

  const items = dashboardQuery.data?.items ?? [];

  // The search query is keyed on the whole request object, so `data` is undefined again after every
  // page/sort/filter change. These two latches are what tell such a refetch apart from a first load:
  // the paginator keeps its size, and the page shell stays put while the grid shows skeletons.
  const [lastTotalCount, setLastTotalCount] = useState(0);
  const [hasLoadedDashboardOnce, setHasLoadedDashboardOnce] = useState(false);
  useEffect(() => {
    if (dashboardQuery.data) {
      setLastTotalCount(dashboardQuery.data.totalCount);
      setHasLoadedDashboardOnce(true);
    }
  }, [dashboardQuery.data]);

  // Global existence flag (ignores current search/filter/sort), used to tell "no results because
  // of applied filters" apart from "this organisation has nothing on the dashboard yet".
  const hasAnyCards = quickStatsQuery.data
    ? Object.values(quickStatsQuery.data.statusCounts).some((count) => count > 0)
    : false;

  const status = (() => {
    if (companyId === undefined || featureFlagsQuery.isLoading) {
      return ProcessStatus.Fetching;
    }

    if (dashboardQuery.isError) {
      return ProcessStatus.Error;
    }

    if (dashboardQuery.isFetching) {
      return ProcessStatus.Fetching;
    }

    return ProcessStatus.Success;
  })();

  // Intentionally surfaces only one toast even if both queries fail (same request is rejected for the same reason).
  const dashboardError = dashboardQuery.error ?? quickStatsQuery.error;

  useEffect(() => {
    if (!dashboardError) {
      return;
    }

    const isForbidden = getApiErrorStatus(dashboardError) === 403;

    toaster({
      severity: 'error',
      summary: isForbidden ? 'Access denied' : t('epdDashboard.onFetchEpds.error'),
      details: getApiErrorDetail(dashboardError) ?? PoorMansError(dashboardError),
      options: { toastId: 'dashboard-error' },
    });
  }, [dashboardError, t]);

  const handleFilter = (e: React.FormEvent<HTMLInputElement>) => {
    setFilter(e.currentTarget.value);
  };

  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummaryModel>();
  const newInvoices = invoiceSummary?.drafts?.length ?? 0;

  const statusOptions: Option[] = useMemo(() => {
    const availableStatuses = new Set(Object.keys(quickStatsQuery.data?.statusCounts ?? {}));
    return allStatusFilters
      .filter((s) => availableStatuses.has(s))
      .map((status) => ({ label: t(`epdWizard.epdState.status.${status}`), value: status }));
  }, [quickStatsQuery.data, t]);

  const formatOptions: Option[] = useMemo(() => {
    const opts: Option[] = [];
    if (quickStatsQuery.data?.hasStandardFormat) opts.push({ label: 'Standard EPD', value: FORMAT_STANDARD });
    if (quickStatsQuery.data?.hasCompilerFormat) opts.push({ label: 'EPD Compiler', value: FORMAT_COMPILER });
    return opts;
  }, [quickStatsQuery.data]);

  const currentUserId = AuthService.getUser()?.id;

  useEffect(() => {
    const fetchInvitationsCount = async () => {
      try {
        setIsInvitationsCountLoading(true);
        const count = await MembershipService.getInvitationsCountByUser();
        setInvitationsCount(count);
      } catch (error) {
        console.error('Failed to fetch invitations count:', error);
      } finally {
        setIsInvitationsCountLoading(false);
      }
    };

    fetchInvitationsCount();
  }, [currentUserId]);

  useEffect(() => {
    const getInvoiceSummary = async () => {
      if (companyAccountId) {
        const summary = await InvoiceService.getInvoiceSummary(companyAccountId);
        setInvoiceSummary(summary);
      }
    };

    getInvoiceSummary();
  }, [companyAccountId]);

  useEffect(() => {
    const feachIsAdminLicenseeInCompany = async () => {
      const result = await AuthService.isAdminLicenseeInCompany(company?.id || '');
      setIsAdminLicenseeCompaies(result);
    };

    feachIsAdminLicenseeInCompany();
  }, [company]);

  const hasAccounts = !!company?.accounts && company.accounts.length > 1;

  const handleListSort = (field: string | undefined, order: 1 | -1 | null) => {
    setListSortField(field);
    setListSortOrder(order);
    setPaginatorFirst(0);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    setPaginatorFirst(0);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filter, typeFilter, formatFilters, dashboardType]);

  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    setPaginatorFirst(event.first);
    setPagination({ page: event.page + 1, pageSize: event.rows });
  };

  const handleRowsChange = (e: DropdownChangeEvent) => {
    const newPageSize = e.value as number;
    setPaginatorFirst(0);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const isCompanyOwner = AuthService.isCompanyOwner(company?.id || '');
  const canCreateEpd = isAdmin || isCompanyOwner || (isLicenseeAdmin && isAdminLicenseeCompanies);

  const getCoverImage = (item: DashboardListItemModel) =>
    item.coverFileId ? FileService.getImageUrl(item.coverFileId) : undefined;

  const dashboardPaginator =
    status !== ProcessStatus.Error && hasCompany && lastTotalCount > 0 ? (
      <DashboardCardsPaginator
        totalCount={lastTotalCount}
        pageSize={pagination.pageSize}
        first={paginatorFirst}
        onPageChange={handlePageChange}
        onRowsChange={handleRowsChange}
        pageSizeOptions={isCardView ? CARD_VIEW_PAGE_SIZE_OPTIONS : LIST_VIEW_PAGE_SIZE_OPTIONS}
        isListView={!isCardView}
      />
    ) : null;

  // The shell cannot render without the company context and feature flags, and an organisation's first
  // result page is included so the dashboard arrives fully formed rather than filling in twice.
  const isPageShellLoading =
    !isCompanyResolved ||
    featureFlagsQuery.isLoading ||
    (isDashboardDataEnabled && !hasLoadedDashboardOnce && status !== ProcessStatus.Error);

  if (isPageShellLoading) {
    return (
      <Container style={{ flexWrap: 'nowrap', position: 'relative', justifyContent: 'center' }}>
        <PageLoader />
      </Container>
    );
  }

  return (
    <Container style={{ flexWrap: 'nowrap', position: 'relative', justifyContent: 'center' }}>
      <ConfirmDialog />
      <MainView>
        <HeadingArea $emptyCompany={showWelcomeToPortal}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasCompany && <DashboardCompanySelector />}
            {hasAccounts && <AccountSwitcher company={company} account={account} />}
          </div>
          {hasCompany && canCreateEpd && <CreateEpd anyEpd={hasAnyCards} isDynamicFormsEnabled={isDynamicFormsEnabled} />}
        </HeadingArea>

        {newInvoices > 0 ? (
          <InfoBox>
            <img src={helpIcon} alt="information" style={{ height: '35px', marginRight: '1rem' }} />
            <div>
              {t('epdDashboard.hasNewInvoices.info_1')}
              <br />
              {t('epdDashboard.hasNewInvoices.info_2')}
            </div>
          </InfoBox>
        ) : null}
        {!showWelcomeToPortal && <DashboardTypeToggle value={dashboardType} onChange={changeDashboardType} />}
        <DashboardLayout>
          <DashboardContent style={!isCardView ? { maxWidth: 'none' } : undefined}>
            {(hasAnyCards || status === ProcessStatus.Fetching || (status === ProcessStatus.Success && hasCompany)) && (
              <DashboardFiltersRow>
                <DashboardFilters
                  searchValue={filter}
                  onSearchChange={handleFilter}
                  onSearchClear={() => setFilter('')}
                  searchPlaceholder={isCopyGroupsView ? 'Search Copy Group' : 'Search EPD'}
                  showOnlySearchFilter={isCopyGroupsView}
                  searchDisabled={!hasAnyCards}
                  statusOptions={statusOptions}
                  selectedStatuses={typeFilter.map((status) => ({
                    label: t(`epdWizard.epdState.status.${status}`),
                    value: status,
                  }))}
                  onStatusChange={(selected) => setTypeFilter(selected.map((x) => x.value as EPDStatusDisplayName))}
                  formatOptions={formatOptions}
                  selectedFormats={formatFilters}
                  onFormatChange={setFormatFilters}
                />
                {!isCopyGroupsView && <DashboardViewToggle value={viewMode} onChange={changeViewMode} />}
                {!isCardView && !!companyId && (isAdmin || isLicenseeAdmin || isCompanyOwner) && (
                  <div style={{ marginLeft: 20 }}>
                    <OrganisationSettingsButton companyId={companyId} />
                  </div>
                )}
              </DashboardFiltersRow>
            )}
            {showWelcomeToPortal && <WelcomeToPortal />}

            {isCardView ? (
              <>
                <CardContainer>
                  {status === ProcessStatus.Fetching && (
                    <>
                      {Array.from({ length: 9 }).map((_, index) => (
                        <Skeleton key={index} width="100%" height="200px" />
                      ))}
                    </>
                  )}

                  {status === ProcessStatus.Error && (
                    <CardsEmpty>
                      <h3>{t('epdDashboard.onFetchEpds.error')}</h3>
                    </CardsEmpty>
                  )}

                  {status === ProcessStatus.Success &&
                    hasCompany &&
                    (items.length > 0 ? (
                      <>
                        {items.map((item) => {
                          if (item.type === DashboardItemType.CopyGroup) {
                            return <EpdGroupCard key={item.id} epdGroup={item} background={null} />;
                          }

                          if (item.type === DashboardItemType.Epd) {
                            return <EpdCard key={item.id} epd={item} background={getCoverImage(item)} />;
                          }

                          return <DynamicEpdCard key={item.id} epd={item} canCopy={canCreateEpd === true} />;
                        })}
                      </>
                    ) : (
                      <NoEPDs anyExist={hasAnyCards} isCopyGroupsView={isCopyGroupsView} />
                    ))}
                </CardContainer>
                {dashboardPaginator}
              </>
            ) : (
              <ListViewContainer>
                {status === ProcessStatus.Fetching && hasCompany && (
                  <DashboardListView
                    pagedCards={[]}
                    loading
                    sortField={listSortField}
                    sortOrder={listSortOrder}
                    onSort={handleListSort}
                  />
                )}

                {status === ProcessStatus.Success && hasCompany && items.length > 0 && (
                  <DashboardListView
                    pagedCards={items}
                    sortField={listSortField}
                    sortOrder={listSortOrder}
                    onSort={handleListSort}
                  />
                )}

                {status === ProcessStatus.Success && hasCompany && items.length === 0 && (
                  <NoEPDs anyExist={hasAnyCards} listView />
                )}

                {dashboardPaginator}
              </ListViewContainer>
            )}
          </DashboardContent>

          {isCardView && (
            <DashboardSideColumn>
              {!!companyId && (isAdmin || isLicenseeAdmin || isCompanyOwner) && (
                <div style={{ marginBottom: 20 }}>
                  <OrganisationSettingsButton companyId={companyId} />
                </div>
              )}
              {!isInvitationsCountLoading && invitationsCount > 0 && <NewInvitesButton count={invitationsCount} />}
              <Sidebox
                quickStats={quickStatsQuery.data}
                companyId={company?.id || ''}
                licenseeSupportEmail={licensee?.supportEmail ?? defaultLicensee.supportEmail}
                isUnderLicensee={!!licensee?.id}
                status={status}
                selectedStatuses={typeFilter}
                onStatusChange={setTypeFilter}
                selectedFormats={formatFilters}
                onFormatChange={setFormatFilters}
                showQuickStats={!isCopyGroupsView}
              />
            </DashboardSideColumn>
          )}
          <FreshworksWidget />
        </DashboardLayout>
      </MainView>
    </Container>
  );
};

export default withGreyBackground(Dashboard);
